import React, { useEffect, useState } from 'react';
import { HeartIcon, BanknotesIcon, ScaleIcon, BuildingLibraryIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

/**
 * Industry cards component displaying different industries with their icons and taglines
 * 
 * @returns {JSX.Element} The rendered IndustryCards component
 */
export default function IndustryCards() {
  // Simple industry card styles
  const industryStyles = {
    healthcare: {
      bg: 'bg-rose-50',
      text: 'text-rose-500',
      icon: 'text-rose-500'
    },
    finance: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-500',
      icon: 'text-emerald-500'
    },
    legal: {
      bg: 'bg-amber-50',
      text: 'text-amber-500',
      icon: 'text-amber-500'
    },
    manufacturing: {
      bg: 'bg-blue-50',
      text: 'text-blue-500',
      icon: 'text-blue-500'
    },
    education: {
      bg: 'bg-purple-50',
      text: 'text-purple-500',
      icon: 'text-purple-500'
    }
  };
  // Animation state for each card
  const [visibleCards, setVisibleCards] = useState<boolean[]>([]);
  // Industry data for the cards with links to industry pages
  const industryData = [
    { 
      industry: 'Healthcare', 
      tagline: 'Protect PHI. Enable clinical AI safely.', 
      path: '/industries/healthcare', 
      iconComponent: HeartIcon,
      styles: industryStyles.healthcare
    },
    { 
      industry: 'Finance', 
      tagline: 'Prevent AI misuse. Stay audit-ready.', 
      path: '/industries/finance', 
      iconComponent: BanknotesIcon,
      styles: industryStyles.finance
    },
    { 
      industry: 'Legal', 
      tagline: 'Govern AI for contract and case data.', 
      path: '/industries/legal', 
      iconComponent: ScaleIcon,
      styles: industryStyles.legal
    },
    { 
      industry: 'Manufacturing', 
      tagline: 'Standardize AI usage across operations.', 
      path: '/industries/manufacturing', 
      iconComponent: BuildingLibraryIcon,
      styles: industryStyles.manufacturing
    },
    { 
      industry: 'Education', 
      tagline: 'Secure LLM usage in research & learning.', 
      path: '/industries/education', 
      iconComponent: AcademicCapIcon,
      styles: industryStyles.education
    },
  ];

  // Animation effect to stagger the appearance of cards
  useEffect(() => {
    // Reset visibility state when component mounts
    setVisibleCards([]);
    
    // Stagger the animation of each card
    industryData.forEach((_, index) => {
      setTimeout(() => {
        setVisibleCards(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, 150 * index); // 150ms delay between each card
    });
    
    // Cleanup function
    return () => {
      setVisibleCards([]);
    };
  }, []);
  
  return (
    <div className="w-full space-y-2">
      {industryData.map((item, index) => (
        <div 
          key={index} 
          className={`${item.styles.bg} rounded-md p-2.5 flex items-center justify-between shadow-sm hover:shadow-md hover:bg-white transition-all duration-300 cursor-pointer group relative overflow-hidden ${visibleCards[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ 
            transitionDelay: `${index * 50}ms` 
          }}
          onClick={() => {
            try {
              // Navigate to the industry page
              window.location.href = item.path;
              // Log card click
              console.log(`${item.industry} industry card clicked`);
            } catch (error) {
              // Log any errors during click handling
              console.error(`Error handling ${item.industry} industry card click:`, error);
            }
          }}
        >
          <div className="flex items-center">
            <div 
              className="w-8 h-8 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-md transition-all duration-300"
            >
              {React.createElement(item.iconComponent, { className: `h-4 w-4 ${item.styles.icon}` })}
            </div>
          </div>
          
          <div className="flex-1 mx-3">
            <span className="text-sm font-medium text-gray-800">{item.industry}</span>
            <p className="text-xs text-gray-500 mt-0.5 leading-tight">{item.tagline}</p>
          </div>
          
          <div>
            <a 
              href={item.path} 
              className={`text-xs ${item.styles.text} flex items-center gap-1 font-medium`}
              onClick={(e) => {
                try {
                  // Prevent default to avoid double navigation
                  e.stopPropagation();
                  // Log industry link click
                  console.log(`${item.industry} industry link clicked`);
                } catch (error) {
                  // Log any errors during click handling
                  console.error(`Error handling ${item.industry} industry link click:`, error);
                }
              }}
            >
              <span>Explore Solutions</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
